import React, { Children } from 'react';
type DivProps = React.HTMLAttributes<HTMLDivElement>;
/**
 * Card — the shared surface for every widget across the platform.
 * Compose with CardHeader, CardContent and CardFooter so each widget has a
 * consistent anatomy: header (title + optional CTA) · content · optional footer.
 */
export function Card({
  className = '',
  children,
  ...props
}: DivProps) {
  return <section className={`bg-white border border-border rounded-xl overflow-hidden ${className}`} {...props} data-id="element-1143">
      {children}
    </section>;
}
interface CardHeaderProps extends DivProps {
  /** Optional leading icon — rendered in a rounded tile. */
  icon?: React.ReactNode;
  /** Header title. If provided, the standard header layout is used. */
  title?: React.ReactNode;
  /** Optional secondary line under the title. */
  subtitle?: React.ReactNode;
  /** Right-aligned actions (CTA buttons, links, counts…). */
  action?: React.ReactNode;
  /** Heading element for the title (a11y). Defaults to h3. */
  as?: 'h2' | 'h3' | 'h4';
  /** Show the bottom divider. Defaults to true. */
  divider?: boolean;
}
export function CardHeader({
  icon,
  title,
  subtitle,
  action,
  as: Heading = 'h3',
  divider = true,
  className = '',
  children,
  ...props
}: CardHeaderProps) {
  const dividerCls = divider ? 'border-b border-border' : '';
  // Children-driven header (full custom content)
  if (!title && children) {
    return <div className={`flex items-center justify-between gap-3 px-4 py-3 ${dividerCls} ${className}`} {...props} data-id="element-1144">
        {children}
      </div>;
  }
  // Prop-driven standard header
  return <div className={`flex items-center justify-between gap-3 px-4 py-3 ${dividerCls} ${className}`} {...props} data-id="element-1145">
      <div className="flex items-center gap-2.5 min-w-0" data-id="element-1146">
        {icon && <span className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center text-foreground" data-id="element-1147">
            {icon}
          </span>}
        <div className="min-w-0" data-id="element-1148">
          {title && <Heading className="text-sm font-semibold text-foreground truncate" data-id="element-1149">
              {title}
            </Heading>}
          {subtitle && <p className="text-[11px] text-muted-foreground mt-0.5 truncate" data-id="element-1150">
              {subtitle}
            </p>}
        </div>
      </div>
      {action && <div className="flex-shrink-0 flex items-center gap-2" data-id="element-1151">{action}</div>}
    </div>;
}
interface CardContentProps extends DivProps {
  /** Remove default padding (for edge-to-edge lists, maps, etc.). */
  noPadding?: boolean;
}
export function CardContent({
  noPadding = false,
  className = '',
  children,
  ...props
}: CardContentProps) {
  return <div className={`${noPadding ? '' : 'p-4'} ${className}`} {...props} data-id="element-1152">
      {children}
    </div>;
}
export function CardFooter({
  className = '',
  children,
  ...props
}: DivProps) {
  return <div className={`flex items-center justify-between gap-3 px-4 py-3 border-t border-border ${className}`} {...props} data-id="element-1153">
      {children}
    </div>;
}